import React, { useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import {
  FaDownload,
  FaUpload,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCopy,
  FaSpinner,
  FaFileExcel,
  FaTrashAlt,
  FaStore,
  FaInfoCircle,
  FaLink,
  FaRulerCombined,
  FaCube,
} from 'react-icons/fa';
import {
  downloadBulkProductSample,
  validateBulkProducts,
  bulkCreateProducts,
} from '../../services/ClientService';
import { parseBulkProductExcel, formatRupee, isBulkSpreadsheetFile } from '../../utils/bulkProductExcel';

const StatusChip = ({ label, count, tone }) => {
  const tones = {
    ready: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    error: 'bg-rose-50 text-rose-800 border-rose-200',
    duplicate: 'bg-amber-50 text-amber-900 border-amber-200',
    muted: 'bg-slate-50 text-slate-700 border-slate-200',
  };
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-sm font-medium ${tones[tone] || tones.muted}`}>
      <span>{label}</span>
      <span className="font-bold tabular-nums">{count}</span>
    </div>
  );
};

const ChannelBadges = ({ channels }) => (
  <div className="flex flex-wrap gap-1.5">
    {channels?.online ? (
      <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-sky-100 text-sky-800">Online</span>
    ) : null}
    {channels?.offline ? (
      <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-violet-100 text-violet-800">Offline</span>
    ) : null}
    {channels?.marketplace ? (
      <span className="text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-teal-100 text-teal-800">Marketplace</span>
    ) : null}
  </div>
);

const InventoryModeBadge = ({ mode }) => {
  if (mode === 'common-inventory') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
        <FaLink className="opacity-80" /> Shared stock (Common Inventory)
      </span>
    );
  }
  if (mode === 'metric-specific') {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">
        <FaRulerCombined className="opacity-80" /> Metric packs (separate stock)
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-slate-100 text-slate-700">
      <FaCube className="opacity-80" /> Unit / piece options
    </span>
  );
};

const BulkProducts = () => {
  const fileInputRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createProgress, setCreateProgress] = useState({ done: 0, total: 0 });
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState(null); // { products, summary }
  const [createResults, setCreateResults] = useState(null);
  const [filter, setFilter] = useState('all'); // all | ready | error | duplicate

  const readyProducts = useMemo(
    () => (preview?.products || []).filter((p) => p.status === 'ready' && p.createPayload),
    [preview]
  );

  const filteredProducts = useMemo(() => {
    const list = preview?.products || [];
    if (filter === 'all') return list;
    return list.filter((p) => p.status === filter);
  }, [preview, filter]);

  const handleDownloadSample = async () => {
    setDownloading(true);
    try {
      const blob = await downloadBulkProductSample();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Zuntly_Bulk_Products_Sample.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Sample Excel downloaded. Fill the Products sheet and upload it here.');
    } catch (e) {
      toast.error(e?.message || 'Could not download sample Excel.');
    } finally {
      setDownloading(false);
    }
  };

  const resetUpload = () => {
    setPreview(null);
    setCreateResults(null);
    setFileName('');
    setFilter('all');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const okExt = isBulkSpreadsheetFile(file);
    if (!okExt) {
      toast.error(
        'Please upload a spreadsheet file (.xlsx, .xls, .numbers, .csv, .ods, etc.).'
      );
      event.target.value = '';
      return;
    }

    setUploading(true);
    setCreateResults(null);
    setFileName(file.name);
    try {
      const rows = await parseBulkProductExcel(file);
      if (!rows.length) {
        toast.error('No product rows found in the Products sheet.');
        setPreview(null);
        return;
      }

      const res = await validateBulkProducts(rows);
      if (!res?.success) {
        toast.error(res?.error || res?.message || 'Validation failed.');
        setPreview(res?.data ? { products: res.data.products || [], summary: res.data.summary } : null);
        return;
      }

      setPreview({
        products: res.data?.products || [],
        summary: res.data?.summary || {},
      });
      const s = res.data?.summary || {};
      toast.success(
        `Checked ${s.totalProducts || 0} product(s): ${s.ready || 0} ready, ${s.errors || 0} with errors, ${s.duplicates || 0} duplicates.`
      );
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Could not read Excel file.');
      setPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmCreate = async () => {
    if (!readyProducts.length) {
      toast.warning('No ready products to create. Fix errors in Excel and upload again.');
      return;
    }

    const payloads = readyProducts.map((p) => p.createPayload);
    setCreating(true);
    setCreateProgress({ done: 0, total: payloads.length });
    setCreateResults(null);

    try {
      // Single batch API — progress is all-or-nothing from client view
      setCreateProgress({ done: 0, total: payloads.length });
      const res = await bulkCreateProducts(payloads);
      setCreateProgress({ done: payloads.length, total: payloads.length });

      if (!res?.success) {
        toast.error(res?.error || res?.message || 'Bulk create failed.');
        return;
      }

      const results = res.data?.results || [];
      setCreateResults(results);
      const created = res.data?.created ?? results.filter((r) => r.success).length;
      const failed = res.data?.failed ?? results.filter((r) => !r.success).length;

      if (created && !failed) {
        toast.success(`Created ${created} product(s) successfully.`);
      } else if (created && failed) {
        toast.warning(`Created ${created} product(s). ${failed} failed — see details below.`);
      } else {
        toast.error(`No products created. ${failed} failed.`);
      }

      // Remove successfully created from ready preview for clarity
      if (created) {
        const okNames = new Set(
          results.filter((r) => r.success).map((r) => String(r.productName || '').toLowerCase())
        );
        setPreview((prev) => {
          if (!prev) return prev;
          const products = prev.products.map((p) => {
            if (p.status === 'ready' && okNames.has(String(p.productName || '').toLowerCase())) {
              return {
                ...p,
                status: 'created',
                errors: [],
                createPayload: null,
              };
            }
            return p;
          });
          const ready = products.filter((p) => p.status === 'ready').length;
          const errors = products.filter((p) => p.status === 'error').length;
          const duplicates = products.filter((p) => p.status === 'duplicate').length;
          return {
            ...prev,
            products,
            summary: { ...prev.summary, ready, errors, duplicates },
          };
        });
      }
    } catch (e) {
      toast.error(e?.message || 'Could not create products.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-teal-50/40">
      <div className="px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
        >
          <div>
            <div className="inline-flex items-center gap-2 text-teal-800 bg-teal-50 border border-teal-100 rounded-full px-3 py-1 text-xs font-semibold mb-3">
              <FaFileExcel />
              Bulk Products
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
              Create many products from Excel
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl text-sm sm:text-base">
              Download the sample sheet, fill product names and packs, upload for a clear preview, then confirm to create.
              Existing products are never changed — duplicates are blocked by product name.
            </p>
          </div>
        </motion.div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5"
          >
            <div className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-2">Step 1</div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Download sample Excel</h2>
            <p className="text-sm text-slate-600 mb-4">
              Includes an <strong>Instructions</strong> sheet and example rows. Green headers = mandatory. Grey = optional.
            </p>
            <button
              type="button"
              onClick={handleDownloadSample}
              disabled={downloading}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold text-sm disabled:opacity-60"
            >
              {downloading ? <FaSpinner className="animate-spin" /> : <FaDownload />}
              {downloading ? 'Preparing…' : 'Download sample'}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:col-span-2"
          >
            <div className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-2">Step 2</div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Upload your filled Excel</h2>
            <p className="text-sm text-slate-600 mb-4">
              We check every row against your catalog, GST groups, metrics, and channels — then show what will be created.
            </p>

            <div
              className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${
                uploading ? 'border-teal-400 bg-teal-50/50' : 'border-slate-300 hover:border-teal-500 hover:bg-teal-50/30'
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xlsm,.xlsb,.xls,.xltx,.xltm,.csv,.ods,.xml,.numbers,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,application/vnd.ms-excel.sheet.binary.macroEnabled.12,application/vnd.oasis.opendocument.spreadsheet,application/vnd.apple.numbers,application/x-iwork-numbers-sffnumbers,text/csv,application/csv,text/xml,application/xml"
                className="absolute inset-0 opacity-0 cursor-pointer"
                onChange={handleFileSelected}
                disabled={uploading || creating}
              />
              <div className="flex flex-col items-center gap-2 pointer-events-none">
                {uploading ? (
                  <FaSpinner className="text-3xl text-teal-700 animate-spin" />
                ) : (
                  <FaUpload className="text-3xl text-slate-400" />
                )}
                <p className="font-semibold text-slate-800">
                  {uploading ? 'Reading & validating…' : 'Drop Excel / Numbers here or click to browse'}
                </p>
                <p className="text-xs text-slate-500">
                  .xlsx · .xls · .numbers · .csv · .ods · max ~500 rows / 200 products
                </p>
                {fileName ? (
                  <p className="text-xs font-medium text-teal-800 mt-1">Selected: {fileName}</p>
                ) : null}
              </div>
            </div>

            {(preview || fileName) && (
              <button
                type="button"
                onClick={resetUpload}
                className="mt-3 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-rose-700"
              >
                <FaTrashAlt /> Clear upload
              </button>
            )}
          </motion.div>
        </div>

        {/* Tips */}
        <div className="flex gap-3 items-start bg-white/80 border border-slate-200 rounded-xl px-4 py-3 text-sm text-slate-600">
          <FaInfoCircle className="text-teal-700 mt-0.5 shrink-0" />
          <p>
            Metric and GST Group names must match what you already set up. Use <strong>Common Inventory (Yes/No)</strong> only when your Metric has a sub-unit (e.g. Kg + gm) — Yes = shared stock across packs; No = separate stock per pack. Unit/Pcs metrics ignore weight columns. Images are not in Excel — add them later from Edit Product if needed.
          </p>
        </div>

        {/* Preview */}
        <AnimatePresence>
          {preview ? (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="space-y-4"
            >
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div>
                    <div className="text-xs font-bold text-teal-700 uppercase tracking-wider mb-1">Step 3</div>
                    <h2 className="text-lg font-semibold text-slate-900">Review before creating</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Only <strong>Ready</strong> products will be created. Fix Excel rows for Errors / Duplicates and re-upload.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <StatusChip label="Ready" count={preview.summary?.ready || 0} tone="ready" />
                    <StatusChip label="Errors" count={preview.summary?.errors || 0} tone="error" />
                    <StatusChip label="Duplicates" count={preview.summary?.duplicates || 0} tone="duplicate" />
                    <StatusChip label="Products" count={preview.summary?.totalProducts || 0} tone="muted" />
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {['all', 'ready', 'error', 'duplicate'].map((key) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFilter(key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize ${
                        filter === key
                          ? 'bg-slate-900 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {key}
                    </button>
                  ))}
                </div>

                <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                  <p className="text-sm text-slate-600">
                    {readyProducts.length} product(s) ready to create
                    {creating ? ` · Creating ${createProgress.done}/${createProgress.total}…` : ''}
                  </p>
                  <button
                    type="button"
                    onClick={handleConfirmCreate}
                    disabled={creating || !readyProducts.length}
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {creating ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
                    {creating ? 'Creating…' : 'Confirm & Create products'}
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {filteredProducts.length === 0 ? (
                  <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500">
                    No products in this filter.
                  </div>
                ) : (
                  filteredProducts.map((product, idx) => {
                    const border =
                      product.status === 'ready'
                        ? 'border-emerald-200'
                        : product.status === 'duplicate'
                          ? 'border-amber-200'
                          : product.status === 'created'
                            ? 'border-sky-200'
                            : 'border-rose-200';
                    const badge =
                      product.status === 'ready'
                        ? 'bg-emerald-100 text-emerald-800'
                        : product.status === 'duplicate'
                          ? 'bg-amber-100 text-amber-900'
                          : product.status === 'created'
                            ? 'bg-sky-100 text-sky-900'
                            : 'bg-rose-100 text-rose-800';

                    return (
                      <motion.div
                        key={`${product.productName}-${idx}`}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(idx * 0.03, 0.3) }}
                        className={`bg-white rounded-2xl border ${border} shadow-sm overflow-hidden`}
                      >
                        <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                          <div className="space-y-2 min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h3 className="text-base sm:text-lg font-bold text-slate-900 truncate">
                                {product.productName}
                              </h3>
                              <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${badge}`}>
                                {product.status}
                              </span>
                            </div>
                            <ChannelBadges channels={product.channels} />
                            <InventoryModeBadge mode={product.inventoryMode} />
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                              {product.metricLabel ? (
                                <span>
                                  Metric: <strong className="text-slate-800">{product.metricLabel}</strong>
                                </span>
                              ) : null}
                              {product.gstGroupName ? (
                                <span>
                                  GST: <strong className="text-slate-800">{product.gstGroupName}</strong>
                                </span>
                              ) : null}
                              {product.categoryName ? (
                                <span>
                                  Category: <strong className="text-slate-800">{product.categoryName}</strong>
                                </span>
                              ) : null}
                              <span className="inline-flex items-center gap-1">
                                <FaStore className="opacity-60" />
                                {product.optionCount || product.options?.length || 0} pack(s)
                              </span>
                            </div>
                            {product.description ? (
                              <p className="text-sm text-slate-500 line-clamp-2">{product.description}</p>
                            ) : null}
                          </div>
                        </div>

                        {product.options?.length ? (
                          <div className="px-4 sm:px-5 pb-4">
                            <div className="overflow-x-auto rounded-xl border border-slate-100">
                              <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 text-slate-600 text-left">
                                  <tr>
                                    <th className="px-3 py-2 font-semibold">Pack</th>
                                    <th className="px-3 py-2 font-semibold">Weight</th>
                                    <th className="px-3 py-2 font-semibold">MRP</th>
                                    <th className="px-3 py-2 font-semibold">Selling</th>
                                    <th className="px-3 py-2 font-semibold">Disc %</th>
                                    <th className="px-3 py-2 font-semibold">Row</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {product.options.map((opt, i) => (
                                    <tr key={`${opt.packLabel}-${i}`} className="border-t border-slate-100">
                                      <td className="px-3 py-2 font-medium text-slate-800">{opt.packLabel}</td>
                                      <td className="px-3 py-2 text-slate-600 tabular-nums">
                                        {opt.metricLabel
                                          ? `${opt.weightValue} ${opt.metricLabel}${opt.metricType === 'supporting' ? ' (sub)' : ''}`
                                          : '—'}
                                      </td>
                                      <td className="px-3 py-2 tabular-nums">{formatRupee(opt.originalPrice)}</td>
                                      <td className="px-3 py-2 tabular-nums font-semibold text-teal-800">
                                        {formatRupee(opt.sellingPrice)}
                                      </td>
                                      <td className="px-3 py-2 tabular-nums">{opt.discount}%</td>
                                      <td className="px-3 py-2 text-slate-500">{opt.rowNumber}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : null}

                        {product.errors?.length ? (
                          <div className="px-4 sm:px-5 pb-4">
                            <div className="rounded-xl bg-rose-50 border border-rose-100 px-3 py-2 space-y-1">
                              {product.errors.map((err, i) => (
                                <p key={i} className="text-sm text-rose-800 flex gap-2">
                                  <FaExclamationTriangle className="mt-0.5 shrink-0 opacity-80" />
                                  <span>{err}</span>
                                </p>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {product.status === 'duplicate' ? (
                          <div className="px-4 sm:px-5 pb-4">
                            <div className="rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-900 flex gap-2">
                              <FaCopy className="mt-0.5 shrink-0" />
                              A product with this name already exists. Rename in Excel or edit the existing product instead.
                            </div>
                          </div>
                        ) : null}
                      </motion.div>
                    );
                  })
                )}
              </div>

              {createResults?.length ? (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
                  <h3 className="font-semibold text-slate-900 mb-3">Create results</h3>
                  <ul className="space-y-2 text-sm">
                    {createResults.map((r, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-2 ${r.success ? 'text-emerald-800' : 'text-rose-800'}`}
                      >
                        {r.success ? <FaCheckCircle className="mt-0.5" /> : <FaExclamationTriangle className="mt-0.5" />}
                        <span>
                          <strong>{r.productName}</strong>
                          {r.success ? ' — created' : ` — ${r.error || 'failed'}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BulkProducts;
